import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { client } from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { AUTH } from "@/constants/testIds";
import { celebrateSignup } from "@/lib/unlocks";

// AuthCallback — runs whenever a URL contains #session_id=… (Emergent Google return path).
// Handles both admin and member auth via a mode saved in sessionStorage before the redirect.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setError("No session_id found in URL.");
      return;
    }
    const sessionId = decodeURIComponent(m[1]);

    (async () => {
      try {
        const r = await client.post("/auth/session", { session_id: sessionId });
        const mode = sessionStorage.getItem("watuwako:mode") || "";
        const next = sessionStorage.getItem("watuwako:next") || "";
        sessionStorage.removeItem("watuwako:mode");
        sessionStorage.removeItem("watuwako:next");
        // Msg 251 §5 · Continue-with-Google from a deep link (e.g. /app/squad or
        // a shared squad URL) must return the new member to that page even
        // after the intake gate. Stash the destination so JoinIntake picks it
        // up on completion. Only when the destination is a real page (not "/app").
        if (mode !== "admin" && next && next !== "/app" && next !== "/") {
          sessionStorage.setItem("watuwako:post_intake_next", next);
        }

        window.history.replaceState({}, "", "/");
        await refresh();

        if (mode === "admin") {
          if (r.data.is_admin) {
            navigate(next || "/admin/queue", { replace: true });
          } else {
            setError("This Google account isn't on the admin allow-list.");
          }
          return;
        }
        // Wave C · "you're in" celebration · fires only when backend tells us
        // this was a brand new account creation (not a repeat sign-in).
        if (r.data?.is_new_account) celebrateSignup();
        // Member mode (or unknown mode with a member session)
        navigate(next || "/app", { replace: true });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Sign-in failed.";
        setError(typeof msg === "string" ? msg : "Sign-in failed.");
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="theme-night flex min-h-screen items-center justify-center" style={{ background: "var(--bg-night)", color: "var(--text-night)" }}>
      <div data-testid={AUTH.authCallback} className="max-w-md rounded-2xl border p-8 text-center" style={{ borderColor: "var(--line-night)", background: "var(--card-night)" }}>
        <span className="compass-mark mx-auto" />
        <p className="eyebrow mt-4" style={{ color: "var(--text-muted-night)" }}>watu wako</p>
        <h1 className="font-display mt-2 text-2xl">{error ? "Sign-in failed" : "Signing you in…"}</h1>
        {error && (
          <p className="mt-4 text-sm" style={{ color: "#FF6B9D" }}>
            {error} <a href="/join" className="underline">Try again</a>
          </p>
        )}
      </div>
    </div>
  );
}
